import { useLayoutEffect, useRef, useState, type KeyboardEvent } from "react";
import { formatCurrency, formatSignedCurrency } from "@/utils/formatters";

export type FlowSeries = "expense" | "income" | "both";

export interface FlowChartWideProps {
  labels: string[];
  expense: number[];
  income: number[];
  /** Qué series se dibujan: gasto, ingreso o ambas. */
  show: FlowSeries;
  /** Mes resaltado al abrir la gráfica (guía vertical y tooltip). */
  activeIndex?: number;
  ariaLabel: string;
  seriesLabels: { expense: string; income: string };
}

type Point = { x: number; y: number };

const TOOLTIP_WIDTH = 176;
const MIN_WIDTH = 280;
const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

// Monotone cubic (Fritsch-Carlson). Unlike Catmull-Rom it never overshoots the data, so a
// series that touches 0 never dips below it and flat stretches stay flat.
const buildTangents = (points: Point[]) => {
  const slopes = points.slice(0, -1).map((point, index) => {
    const run = points[index + 1].x - point.x;
    return run === 0 ? 0 : (points[index + 1].y - point.y) / run;
  });

  const tangents = points.map((_, index) => {
    if (index === 0) return slopes[0];
    if (index === points.length - 1) return slopes[slopes.length - 1];
    return (slopes[index - 1] + slopes[index]) / 2;
  });

  slopes.forEach((slope, index) => {
    if (slope === 0) {
      tangents[index] = 0;
      tangents[index + 1] = 0;
      return;
    }

    let start = tangents[index] / slope;
    let end = tangents[index + 1] / slope;
    if (start < 0) {
      tangents[index] = 0;
      start = 0;
    }
    if (end < 0) {
      tangents[index + 1] = 0;
      end = 0;
    }

    const magnitude = start * start + end * end;
    if (magnitude > 9) {
      const factor = 3 / Math.sqrt(magnitude);
      tangents[index] = factor * start * slope;
      tangents[index + 1] = factor * end * slope;
    }
  });

  return tangents;
};

const buildSmoothPath = (points: Point[]) => {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;

  const tangents = buildTangents(points);
  let path = `M ${points[0].x} ${points[0].y}`;

  for (let index = 0; index < points.length - 1; index += 1) {
    const current = points[index];
    const next = points[index + 1];
    const third = (next.x - current.x) / 3;
    const controlPoint1Y = current.y + tangents[index] * third;
    const controlPoint2Y = next.y - tangents[index + 1] * third;
    path += ` C ${current.x + third} ${controlPoint1Y}, ${next.x - third} ${controlPoint2Y}, ${next.x} ${next.y}`;
  }

  return path;
};

const buildLinePath = (points: Point[]) =>
  points.map((point, index) => `${index === 0 ? "M" : "L"}${point.x} ${point.y}`).join(" ");

/**
 * FlowChartWide: flujo mensual de una categoría. Gasto en línea sólida con círculos; ingreso
 * en línea discontinua con cuadrados, así las series se distinguen por trazo y forma y no solo
 * por color. El tooltip sigue al puntero o al teclado (flechas, Inicio y Fin).
 */
export function FlowChartWide({ labels, expense, income, show, activeIndex, ariaLabel, seriesLabels }: FlowChartWideProps) {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [picked, setPicked] = useState<number | null>(null);

  useLayoutEffect(() => {
    const element = containerRef.current;
    if (!element) return;
    const updateWidth = () => setContainerWidth(Math.max(element.clientWidth - 24, 0));
    updateWidth();
    const observer = new ResizeObserver(() => updateWidth());
    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const count = labels.length;
  if (count === 0) return null;

  const showExpense = show !== "income";
  const showIncome = show !== "expense";
  const active = picked ?? activeIndex ?? null;

  // The viewBox matches the real width so text keeps its size on phones instead of scaling
  // down with a fixed 1200px canvas.
  const width = Math.max(containerWidth, MIN_WIDTH);
  const height = clamp(width * 0.3, 220, 360);
  const maxValue = Math.max(
    0,
    ...(showExpense ? expense : []),
    ...(showIncome ? income : []),
  );
  const domainMax = maxValue > 0 ? maxValue * 1.3 : 100;
  const yTicks = [0, 1, 2, 3].map((index) => (domainMax / 3) * index);
  const tickLabels = yTicks.map((tick) => formatCurrency(tick, undefined, { maximumFractionDigits: 0, minimumFractionDigits: 0 }));
  const gutter = clamp(Math.ceil(Math.max(...tickLabels.map((label) => label.length)) * 7) + 16, 48, 104);
  const margin = { top: 16, right: width < 360 ? 12 : 20, bottom: 32, left: gutter };
  const innerWidth = Math.max(width - margin.left - margin.right, 1);
  const innerHeight = height - margin.top - margin.bottom;
  const columnWidth = innerWidth / Math.max(count - 1, 1);

  const x = (index: number) => margin.left + (count === 1 ? innerWidth / 2 : index * columnWidth);
  const y = (value: number) => margin.top + innerHeight - (value / domainMax) * innerHeight;
  const expensePoints = expense.map((value, index) => ({ x: x(index), y: y(value) }));
  const incomePoints = income.map((value, index) => ({ x: x(index), y: y(value) }));

  const onKeyDown = (event: KeyboardEvent<SVGSVGElement>) => {
    const current = active ?? 0;
    const next =
      event.key === "ArrowRight" ? Math.min(current + 1, count - 1)
        : event.key === "ArrowLeft" ? Math.max(current - 1, 0)
          : event.key === "Home" ? 0
            : event.key === "End" ? count - 1
              : -1;
    if (next < 0) return;
    event.preventDefault();
    setPicked(next);
  };

  const tooltipRows = [
    showExpense ? { key: "expense", text: `${seriesLabels.expense} ${formatSignedCurrency(-Math.abs(expense[active ?? 0] ?? 0))}` } : null,
    showIncome ? { key: "income", text: `${seriesLabels.income} ${formatSignedCurrency(Math.abs(income[active ?? 0] ?? 0))}` } : null,
  ].filter((row): row is { key: string; text: string } => row !== null);
  const tooltipHeight = 24 + 20 * tooltipRows.length + 8;
  const tooltipX = active === null ? 0 : clamp(x(active) - TOOLTIP_WIDTH / 2, 8, width - TOOLTIP_WIDTH - 8);

  return (
    <div ref={containerRef} className="w-full min-w-0 rounded-[var(--ds-radius-md)] border-[1.5px] border-solid border-[var(--ds-line)] bg-transparent p-3 text-[var(--ds-ink-muted)]">
      <svg
        role="img"
        tabIndex={0}
        aria-label={ariaLabel}
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        onKeyDown={onKeyDown}
        onPointerLeave={() => setPicked(null)}
        className="app-focus-ring block max-w-full rounded-[var(--ds-radius-md)]"
      >
        {yTicks.map((tick, index) => (
          <g key={`tick-${tick}`}>
            <line
              x1={margin.left}
              x2={width - margin.right}
              y1={y(tick)}
              y2={y(tick)}
              stroke="var(--ds-line)"
              strokeDasharray="4 5"
            />
            <text x={margin.left - 10} y={y(tick) + 4} textAnchor="end" fill="currentColor" fontSize={12}>
              {tickLabels[index]}
            </text>
          </g>
        ))}

        {labels.map((label, index) => (
          <text key={`label-${label}-${index}`} x={x(index)} y={height - 8} textAnchor="middle" fill="currentColor" fontSize={12}>
            {label}
          </text>
        ))}

        {active !== null ? (
          <line x1={x(active)} x2={x(active)} y1={margin.top} y2={margin.top + innerHeight} stroke="var(--ds-line)" />
        ) : null}

        {showIncome ? (
          <path d={buildLinePath(incomePoints)} fill="none" stroke="var(--ds-positive)" strokeWidth={3} strokeDasharray="10 6" strokeLinecap="round" strokeLinejoin="round" />
        ) : null}
        {showIncome
          ? incomePoints.map((point, index) => (
              <rect key={`income-${index}`} x={point.x - (index === active ? 5 : 4)} y={point.y - (index === active ? 5 : 4)} width={index === active ? 10 : 8} height={index === active ? 10 : 8} fill="var(--ds-positive)" />
            ))
          : null}

        {showExpense ? (
          <path d={buildSmoothPath(expensePoints)} fill="none" stroke="var(--ds-card-flame)" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
        ) : null}
        {showExpense
          ? expensePoints.map((point, index) => (
              <circle key={`expense-${index}`} cx={point.x} cy={point.y} r={index === active ? 6 : 4.5} fill="var(--ds-card-flame)" />
            ))
          : null}

        {labels.map((_, index) => (
          <rect
            key={`hit-${index}`}
            x={Math.max(margin.left, x(index) - columnWidth / 2)}
            y={margin.top}
            width={count === 1 ? innerWidth : index === 0 || index === count - 1 ? columnWidth / 2 : columnWidth}
            height={innerHeight}
            fill="transparent"
            onPointerEnter={() => setPicked(index)}
            onPointerMove={() => setPicked(index)}
          />
        ))}

        {active !== null ? (
          <g transform={`translate(${tooltipX.toFixed(1)}, ${margin.top + innerHeight - tooltipHeight - 16})`} aria-hidden="true">
            <rect width={TOOLTIP_WIDTH} height={tooltipHeight} rx={12} fill="var(--ds-ground)" stroke="var(--ds-line)" />
            <text x={14} y={20} fontSize={14} fontWeight={600} fill="var(--ds-ink)">{labels[active]}</text>
            {tooltipRows.map((row, index) => (
              <g key={row.key}>
                {row.key === "expense"
                  ? <circle cx={18} cy={36 + 20 * index} r={4} fill="var(--ds-card-flame)" />
                  : <rect x={14} y={32 + 20 * index} width={8} height={8} fill="var(--ds-positive)" />}
                <text x={30} y={40 + 20 * index} fontSize={13} fill="var(--ds-ink)">{row.text}</text>
              </g>
            ))}
          </g>
        ) : null}
      </svg>


    </div>
  );
}
